import { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Badge, Button, Col, FloatingLabel, Form, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageLayout from "../../../layouts/PageLayout";

const TYPES = [
  { id: 1, name: "Verification" },
  { id: 2, name: "Rescue" },
  { id: 3, name: "Business" },
  { id: 4, name: "Follow Up" },
];

const DistributionRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ type: "", status: "", from_date: "", to_date: "" });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, from: 0 });

  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  // wait for the user to stop typing before hitting the API
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    Object.entries(filters).forEach(([name, value]) => {
      if (value) params[name] = value;
    });

    setLoading(true);
    setError("");
    axios.get(`${process.env.REACT_APP_API_URL}/distribution-records`, { params })
      .then((res) => {
        const data = res.data.records;
        setRecords(data.data);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          total: data.total,
          from: data.from || 0,
        });
      })
      .catch((err) => {
        console.error(err);
        setError("An error occurred while fetching distribution records.");
      })
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilters({ type: "", status: "", from_date: "", to_date: "" });
    setPage(1);
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Distribution Record</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item"><Link to="#" style={{ color: "#077834" }}>home</Link></li>
                <li className="mc-breadcrumb-item"><Link to="#" style={{ color: "#077834" }}>Finances</Link></li>
                <li className="mc-breadcrumb-item">Data Distribution</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-3 g-3">
        <Col md={4}>
          <FloatingLabel label="Search FSF-ID / Name / CNIC / Contact / User">
            <Form.Control
              type="text"
              placeholder="Search FSF-ID / Name / CNIC / Contact / User"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="Distributed For">
            <Form.Select name="type" value={filters.type} onChange={handleFilterChange}>
              <option value="">All</option>
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="Status">
            <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="From Date">
            <Form.Control type="date" name="from_date" value={filters.from_date} onChange={handleFilterChange} />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="To Date">
            <Form.Control type="date" name="to_date" value={filters.to_date} onChange={handleFilterChange} />
          </FloatingLabel>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Total Records: {pagination.total}</h5>
        <Button variant="secondary" size="sm" onClick={handleClear}>Clear</Button>
      </div>

      {error && <p className="text-danger">{error}</p>}

      {loading ? (
        <h5 className="text-center"><Spinner animation="border" variant="primary" /> Loading Data...</h5>
      ) : (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Distributed On</th>
              <th>FSF-ID</th>
              <th>Family Name</th>
              <th>CNIC</th>
              <th>Distributed For</th>
              <th>Assigned To</th>
              <th>Distributed By</th>
              <th>Current Stage</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {records.length > 0 ? (
              records.map((record, index) => (
                <tr key={record.id}>
                  <td>{pagination.from + index}</td>
                  <td>{record.distributed_at ? moment(record.distributed_at).format("DD-MM-YYYY hh:mm A") : "-"}</td>
                  <td>{record.f_number || "-"}</td>
                  <td>{record.beneficiary_name || "N/A"}</td>
                  <td>{record.cnic || "-"}</td>
                  <td>{record.type || "-"}</td>
                  <td>{record.assigned_to || "-"}</td>
                  <td>{record.distributed_by || "-"}</td>
                  <td>{record.stage || "-"}</td>
                  <td>
                    {record.status ? (
                      <Badge bg={record.status === "Completed" ? "success" : "warning"}>{record.status}</Badge>
                    ) : "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">No distribution records found.</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
        <Button
          style={{ backgroundColor: "#077834" }}
          disabled={pagination.current_page <= 1}
          onClick={() => setPage(pagination.current_page - 1)}
        >
          Prev
        </Button>
        <span>Page {pagination.current_page} of {pagination.last_page}</span>
        <Button
          style={{ backgroundColor: "#077834" }}
          disabled={pagination.current_page >= pagination.last_page}
          onClick={() => setPage(pagination.current_page + 1)}
        >
          Next
        </Button>
      </div>
    </PageLayout>
  );
};

export default DistributionRecords;
